import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { FormField } from '@angular/forms/signals';

import { EButtonStyleType, EButtonType, type ESize } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';

import type { ChatBotsButtonSettingsData } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';

import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Edits the widget form's `chatBotsButtonSettings` branch directly. Switching
 * the button type also patches every `chatBots` item's `type`. Save/cancel and
 * the transaction frame come from `FormSettingsPanel`.
 */
@Component({
    selector: 'web-form-chat-bot-button-settings',
    templateUrl: './form-chat-bot-button-settings.component.html',
    styleUrls: ['./form-chat-bot-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, WebCustomButtonComponent, FormField],
})
export class FormChatBotButtonSettingsComponent extends FormSettingsPanel<FormWidgetForm> {
    public readonly EButtonType = EButtonType;
    protected readonly EButtonStyleType = EButtonStyleType;
    protected readonly ERadioGroupType = ERadioGroupType;

    public readonly settings = computed<ChatBotsButtonSettingsData>(() => this.form().chatBotsButtonSettings().value());

    protected get styleType() {
        return this.form().chatBotsButtonSettings.styleType;
    }

    public onSwitchButtonType(type: EButtonType): void {
        const form = this.form();
        const write = () => {
            form.setChatBots(
                form
                    .chatBots()
                    .value()
                    .map((chatBot) => ({ ...chatBot, type: type as ChatBotsButtonSettingsData['type'] }))
            );
            this.patch({ type: type as ChatBotsButtonSettingsData['type'] });
        };

        // Batch the array + settings writes; join an open panel transaction.
        if (form.inTransaction()) {
            write();
        } else {
            form.transact(write);
        }
    }

    public setButtonSize(value: ESize): void {
        this.patch({ buttonSize: value as ChatBotsButtonSettingsData['buttonSize'] });
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<ChatBotsButtonSettingsData> = {
            buttonBase: changes.base as unknown as ChatBotsButtonSettingsData['buttonBase'],
            buttonHover: changes.hover as unknown as ChatBotsButtonSettingsData['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as ChatBotsButtonSettingsData['advancedRadius'];
        }
        if (changes?.advancedBorderSettings !== undefined) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as unknown as ChatBotsButtonSettingsData['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    private patch(patch: Partial<ChatBotsButtonSettingsData>): void {
        this.form().setChatBotsButtonSettings({ ...this.settings(), ...patch });
    }
}
